import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core'
import { ArrowBack as ArrowLeft } from '@material-ui/icons'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { AxiosResponse } from 'axios'
import { toast } from 'react-toastify'

import { TabPanel } from '../../../components/TabPanel/TabPanel'
import { CampaignSetupTab } from '../../Outbound/Campaign/CreateCampaign/CampaignSetupTab'
import { CampaignSequenceTab } from '../../Outbound/Campaign/CreateCampaign/CampaignSequenceTab'
import {
    CampaignData,
    CTAOption,
    SequenceItem,
    SequenceStep,
} from '../../Outbound/Campaign/CreateCampaign/types'
import { ProspectTrackingTab } from '../../Outbound/Campaign/CampaignDetail/ProspectTrackingTab'
import { RecentActivityTab } from '../../Outbound/Campaign/CampaignDetail/RecentActivityTab'
import { useCampaignDetail } from '../../../hooks/Outreach/useCampaign'
import {
    Campaign,
    CampaignProspect,
    CampaignProspectActivity,
    CampaignStatus,
} from '../../../models/Outreach/types'
import { SeasonCampaignReviewTab } from './components/SeasonCampaignReviewTab'
import {
    getPrimaryCampaignDisplayStatus,
    PRIMARY_CAMPAIGN_STATUS_LABELS,
} from '../utils/campaignDisplayStatus'
import {
    useSeasonDetail,
    useSeasonIdFromQuery,
} from './hooks/useSeasonCampaigns'
import {
    campaignStepsToSequence,
    channelsFromCampaign,
    getSeasonCampaignActivationBlockers,
    isSeasonCampaignSetupComplete,
    leasingSeasonOutreachPath,
    parseSeasonCampaignTabFromSearch,
    seasonCampaignDetailPath,
    seasonCampaignsListPath,
    seasonCampaignTabSlugForIndex,
    sequenceToUpdateSteps,
    SeasonCampaignDetailTab,
} from './utils/seasonCampaignUtils'

const useStyles = makeStyles((theme: Theme) => ({
    container: {
        width: '100%',
        padding: theme.spacing(3),
        maxWidth: 1400,
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(3),
        flexWrap: 'wrap',
        gap: theme.spacing(2),
    },
    headerContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    title: {
        fontSize: '2rem',
        fontWeight: 'bold',
        color: '#1f2937',
    },
    subtitle: {
        color: '#6b7280',
        fontSize: '1rem',
    },
    backButton: {
        borderRadius: '8px',
        padding: '8px 16px',
        fontSize: '0.875rem',
        fontWeight: 500,
        borderColor: '#d1d5db',
        color: '#6b7280',
        textTransform: 'none',
        alignSelf: 'flex-start',
        '&:hover': {
            borderColor: '#3b82f6',
            color: '#3b82f6',
            backgroundColor: 'rgba(59, 130, 246, 0.04)',
        },
    },
    tabsContainer: {
        backgroundColor: '#ffffff',
        border: '1px solid rgba(226, 232, 240, 0.5)',
        borderRadius: theme.spacing(1),
        boxShadow:
            '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: theme.spacing(3),
        padding: theme.spacing(0.5),
    },
    tab: {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        minWidth: 120,
        borderRadius: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        padding: theme.spacing(1, 2),
        transition: 'all 0.2s ease',
        '&:hover': {
            backgroundColor: 'rgba(59, 130, 246, 0.05)',
        },
        '&.Mui-selected': {
            backgroundColor: '#3b82f6',
            color: 'white',
            fontWeight: 600,
        },
        '&.Mui-disabled': {
            opacity: 0.5,
        },
    },
    statusActive: {
        backgroundColor: '#ecfdf5',
        color: '#047857',
    },
    statusReady: {
        backgroundColor: '#eff6ff',
        color: '#1d4ed8',
    },
    statusSetup: {
        backgroundColor: '#fef3c7',
        color: '#b45309',
    },
}))

const ctaOptions: CTAOption[] = [
    { value: 'schedule-tour', label: 'Schedule a Tour' },
    { value: 'request-info', label: 'Request More Info' },
    { value: 'apply-now', label: 'Apply Now' },
    { value: 'call-us', label: 'Call Us Today' },
]

const MONITOR_TAB_INDEXES = new Set([3, 4])

function isMessageStep(item: SequenceItem): item is SequenceStep {
    return item.type === 'email' || item.type === 'sms'
}

function isMonitorTab(index: number): boolean {
    return MONITOR_TAB_INDEXES.has(index)
}

export const SeasonCampaignDetail: React.FC = () => {
    const classes = useStyles()
    const { id } = useParams<{ id: string }>()
    const location = useLocation()
    const history = useHistory()
    const seasonId = useSeasonIdFromQuery(location.search)
    const seasonQuery = useSeasonDetail(seasonId)
    const season = seasonQuery.data

    const {
        campaignDetail,
        isLoadingCampaignDetail,
        errorCampaignDetail,
        updateCampaign,
        updateCampaignStatus,
        campaignProspects,
        isLoadingCampaignProspects,
        errorCampaignProspects,
        campaignProspectActivities,
        isLoadingCampaignProspectActivities,
        errorCampaignProspectActivities,
    } = useCampaignDetail(id)

    const [activeTab, setActiveTab] = useState(() =>
        parseSeasonCampaignTabFromSearch(location.search),
    )
    const [campaignData, setCampaignData] = useState<CampaignData>({
        name: '',
        channels: { email: true, sms: false },
        cta: 'schedule-tour',
        reply_to_email: '',
        sender_name: '',
        concessions: [],
    })
    const [sequence, setSequence] = useState<SequenceItem[]>([])
    const [initialized, setInitialized] = useState(false)

    const syncLocalStateFromCampaign = useCallback((campaign: Campaign) => {
        setCampaignData({
            name: campaign.name || '',
            channels: channelsFromCampaign(campaign),
            cta: campaign.campaign_goal || 'schedule-tour',
            reply_to_email: campaign.reply_to_email || '',
            sender_name: campaign.sender_name || '',
            concessions: campaign.concessions ?? [],
        })
        setSequence(campaignStepsToSequence(campaign.steps ?? []))
        setInitialized(true)
    }, [])

    useEffect(() => {
        if (!campaignDetail || initialized) return
        syncLocalStateFromCampaign(campaignDetail)
    }, [campaignDetail, initialized, syncLocalStateFromCampaign])

    useEffect(() => {
        setInitialized(false)
    }, [id])

    const isSystemCampaign = !!campaignDetail?.system_kind

    const isSetupComplete = useCallback(() => {
        return (
            campaignData.name.trim() !== '' &&
            campaignData.reply_to_email.trim() !== '' &&
            campaignData.sender_name.trim() !== '' &&
            (isSystemCampaign ||
                campaignData.channels.email ||
                campaignData.channels.sms)
        )
    }, [campaignData, isSystemCampaign])

    const isSequenceComplete = useCallback(() => {
        const messageSteps = sequence.filter(isMessageStep)
        return (
            messageSteps.length > 0 &&
            messageSteps.every((step) => !!step.guest_card)
        )
    }, [sequence])

    const canOpenTab = useCallback(
        (tabIndex: number) => {
            if (isMonitorTab(tabIndex) || tabIndex === 0) return true
            if (tabIndex === 1) return isSetupComplete()
            if (tabIndex === 2) {
                return isSetupComplete() && isSequenceComplete()
            }
            return false
        },
        [isSetupComplete, isSequenceComplete],
    )

    const replaceTabInUrl = useCallback(
        (tabIndex: number) => {
            const slug = seasonCampaignTabSlugForIndex(tabIndex)
            const nextPath = seasonCampaignDetailPath(
                id,
                seasonId,
                slug === 'setup' ? null : (slug as SeasonCampaignDetailTab),
            )
            if (`${location.pathname}${location.search}` !== nextPath) {
                history.replace(nextPath)
            }
        },
        [history, id, location.pathname, location.search, seasonId],
    )

    const navigateToTab = useCallback(
        (tabIndex: number) => {
            if (!canOpenTab(tabIndex)) return
            setActiveTab(tabIndex)
            replaceTabInUrl(tabIndex)
        },
        [canOpenTab, replaceTabInUrl],
    )

    useEffect(() => {
        setActiveTab(parseSeasonCampaignTabFromSearch(location.search))
    }, [location.search])

    // After campaign hydration, clamp gated configure tabs if the URL is ahead
    // of setup progress. Monitor tabs are never clamped.
    useEffect(() => {
        if (!initialized) return
        if (!canOpenTab(activeTab)) {
            replaceTabInUrl(0)
        }
    }, [initialized, activeTab, canOpenTab, replaceTabInUrl])

    const canActivate = isSeasonCampaignSetupComplete(
        campaignData.name,
        campaignData.reply_to_email,
        campaignData.sender_name,
        sequence,
    )

    const activationBlockers = getSeasonCampaignActivationBlockers(
        campaignData.name,
        campaignData.reply_to_email,
        campaignData.sender_name,
        sequence,
    )

    const handleSaveSetup = (options?: { onSuccess?: () => void }) => {
        if (!id || !campaignDetail) return

        if (!isSetupComplete()) {
            toast.error('Complete campaign details before continuing')
            return
        }

        updateCampaign.mutate(
            {
                id: parseInt(id, 10),
                name: campaignData.name.trim(),
                replyToEmail: campaignData.reply_to_email.trim(),
                senderName: campaignData.sender_name.trim(),
                campaignGoal: campaignData.cta,
                concessions: campaignData.concessions,
            },
            {
                onSuccess: (response: AxiosResponse<Campaign>) => {
                    syncLocalStateFromCampaign(response.data)
                    options?.onSuccess?.()
                },
            },
        )
    }

    const handleSaveSequence = (options?: { onSuccess?: () => void }) => {
        if (!id || !campaignDetail) return

        if (!isSequenceComplete()) {
            toast.error(
                'Add at least one message step with a guest card before continuing',
            )
            return
        }

        const rawSteps = sequenceToUpdateSteps(
            sequence,
            campaignDetail.steps ?? [],
        )

        updateCampaign.mutate(
            {
                id: parseInt(id, 10),
                name: campaignData.name.trim(),
                replyToEmail: campaignData.reply_to_email.trim(),
                senderName: campaignData.sender_name.trim(),
                campaignGoal: campaignData.cta,
                concessions: campaignData.concessions,
                rawSteps,
            },
            {
                onSuccess: (response: AxiosResponse<Campaign>) => {
                    syncLocalStateFromCampaign(response.data)
                    options?.onSuccess?.()
                },
            },
        )
    }

    const handleActivate = () => {
        if (!id) return
        if (!canActivate) {
            toast.error(
                'Complete setup and sequence before activating (real reply-to, steps with guest cards).',
            )
            return
        }

        handleSaveSequence({
            onSuccess: () => {
                updateCampaignStatus.mutate({
                    id: parseInt(id, 10),
                    status: CampaignStatus.ACTIVE,
                })
            },
        })
    }

    const handlePause = () => {
        if (!id) return
        updateCampaignStatus.mutate({
            id: parseInt(id, 10),
            status: CampaignStatus.PAUSED,
        })
    }

    const handleTabChange = (_: React.ChangeEvent<{}>, newValue: number) => {
        navigateToTab(newValue)
    }

    const prospects: CampaignProspect[] =
        (campaignProspects as { data?: CampaignProspect[] } | undefined)
            ?.data || []

    const activities: CampaignProspectActivity[] =
        (campaignProspectActivities as
            | { data?: CampaignProspectActivity[] }
            | undefined)?.data || []

    const recentActivity = useMemo(() => {
        return activities.map((activity) => {
            let activityType: 'conversion' | 'click' | 'open' | 'sent' = 'sent'

            if (activity.type === 'email_clicked') {
                activityType = 'click'
            } else if (activity.type === 'email_opened') {
                activityType = 'open'
            } else if (
                activity.type === 'email_sent' ||
                activity.type === 'text_sent'
            ) {
                activityType = 'sent'
            }

            return {
                type: activityType,
                prospect: activity.identifier,
                action: activity.action,
                time: activity.relative_time,
            }
        })
    }, [activities])

    const displayStatus = campaignDetail
        ? getPrimaryCampaignDisplayStatus(campaignDetail)
        : 'needs_setup'
    const statusLabel = PRIMARY_CAMPAIGN_STATUS_LABELS[displayStatus]
    const statusClass =
        displayStatus === 'active'
            ? classes.statusActive
            : displayStatus === 'ready_paused'
            ? classes.statusReady
            : classes.statusSetup

    const isSaving = updateCampaign.isLoading || updateCampaignStatus.isLoading

    const subtitle = isMonitorTab(activeTab)
        ? "See who's enrolled and how messages are performing for this season campaign."
        : 'Configure sender details and message sequence. Prospects auto-enroll when enriched; messages send once activated.'

    if (isLoadingCampaignDetail) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={400}
            >
                <CircularProgress />
            </Box>
        )
    }

    if (errorCampaignDetail || !campaignDetail) {
        return (
            <Box className={classes.container}>
                <Typography color="error">Failed to load campaign.</Typography>
                <Button
                    style={{ marginTop: 16 }}
                    onClick={() =>
                        history.push(seasonCampaignsListPath(seasonId))
                    }
                >
                    Back to campaigns
                </Button>
            </Box>
        )
    }

    if (
        seasonId != null &&
        campaignDetail.leasing_season != null &&
        campaignDetail.leasing_season !== seasonId
    ) {
        return (
            <Box className={classes.container}>
                <Typography color="error">
                    This campaign belongs to a different leasing season.
                </Typography>
                <Button
                    style={{ marginTop: 16 }}
                    onClick={() =>
                        history.push(leasingSeasonOutreachPath(seasonId))
                    }
                >
                    Back to Leasing Season
                </Button>
            </Box>
        )
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.header}>
                <Box className={classes.headerContent}>
                    <Link
                        to={leasingSeasonOutreachPath(seasonId)}
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ArrowLeft />}
                            className={classes.backButton}
                        >
                            Back to Leasing Season
                            {season?.name ? ` · ${season.name}` : ''}
                        </Button>
                    </Link>
                    <Box className={classes.titleRow}>
                        <Typography variant="h4" className={classes.title}>
                            {campaignData.name || campaignDetail.name}
                        </Typography>
                        <Chip
                            label={statusLabel}
                            size="small"
                            className={statusClass}
                        />
                        {isSystemCampaign && (
                            <Chip
                                label="System campaign"
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    <Typography variant="body1" className={classes.subtitle}>
                        {subtitle}
                    </Typography>
                </Box>
            </Box>

            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className={classes.tabsContainer}
                TabIndicatorProps={{ style: { display: 'none' } }}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab label="Campaign Setup" className={classes.tab} />
                <Tab
                    label="Message Sequence"
                    className={classes.tab}
                    disabled={!isSetupComplete()}
                />
                <Tab
                    label="Review & Activate"
                    className={classes.tab}
                    disabled={!isSetupComplete() || !isSequenceComplete()}
                />
                <Tab label="Prospect Tracking" className={classes.tab} />
                <Tab label="Recent Activity" className={classes.tab} />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
                <CampaignSetupTab
                    campaignData={campaignData}
                    setCampaignData={setCampaignData}
                    ctaOptions={ctaOptions}
                    sequence={sequence}
                    lockChannels={isSystemCampaign}
                    readOnlyName={isSystemCampaign}
                    showDraftReplyWarning
                    continueLabel={
                        isSaving ? 'Saving…' : 'Save & continue to sequence →'
                    }
                    onNext={() =>
                        handleSaveSetup({
                            onSuccess: () => navigateToTab(1),
                        })
                    }
                />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <CampaignSequenceTab
                    sequence={sequence}
                    setSequence={setSequence}
                    channels={campaignData.channels}
                    campaignName={campaignData.name}
                    campaignGoal={campaignData.cta}
                    onBack={() => navigateToTab(0)}
                    onNext={() =>
                        handleSaveSequence({
                            onSuccess: () => navigateToTab(2),
                        })
                    }
                    backLabel="Back to setup"
                    nextLabel={
                        isSaving ? 'Saving…' : 'Save & continue to review →'
                    }
                />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
                <SeasonCampaignReviewTab
                    campaignData={campaignData}
                    sequence={sequence}
                    status={campaignDetail.status}
                    canActivate={canActivate}
                    activationBlockers={activationBlockers}
                    onBack={() => navigateToTab(1)}
                    onActivate={handleActivate}
                    onPause={handlePause}
                    isSaving={isSaving}
                />
            </TabPanel>

            <TabPanel value={activeTab} index={3}>
                <ProspectTrackingTab
                    prospects={prospects}
                    isLoading={isLoadingCampaignProspects}
                    error={errorCampaignProspects as Error | null}
                />
            </TabPanel>

            <TabPanel value={activeTab} index={4}>
                <RecentActivityTab
                    recentActivity={recentActivity}
                    isLoading={isLoadingCampaignProspectActivities}
                    error={errorCampaignProspectActivities}
                />
            </TabPanel>
        </Box>
    )
}
